import React, { createContext, useContext, useRef, useState } from 'react';
import { InAppNotification } from '../Components/InAppNotification';

const InAppNotificationContext = createContext({
  showNotification: () => null,
  dismissNotification: () => {},
  clearNotifications: () => {}
});

const DUPLICATE_WINDOW_MS = 45000;
const DEFAULT_DURATION = 5000;
const DEFAULT_ROUTE = '/notificaciones';

const buildDuplicateKey = (notification) => {
  if (notification.notificationId) {
    return `notification:${notification.notificationId}`;
  }

  if (notification.rawPayload) {
    return `payload:${notification.rawPayload}`;
  }

  return `content:${notification.title || ''}:${notification.message || ''}`;
};

const normalizeNotification = (notification) => {
  const title = notification?.title || 'Nueva notificacion';
  const message = notification?.message || notification?.body || '';

  return {
    id:
      notification?.id ||
      notification?.notificationId ||
      `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    notificationId: notification?.notificationId || null,
    title,
    message,
    route: notification?.route || DEFAULT_ROUTE,
    duration:
      typeof notification?.duration === 'number'
        ? notification.duration
        : DEFAULT_DURATION,
    rawPayload: notification?.rawPayload || null,
    additionalData: notification?.additionalData || {},
    source: notification?.source || 'in-app'
  };
};

export const InAppNotificationProvider = ({ children, navigate }) => {
  const [notifications, setNotifications] = useState([]);
  const recentNotificationsRef = useRef(new Map());

  const dismissNotification = (notificationId) => {
    setNotifications((current) =>
      current.filter((notification) => notification.id !== notificationId)
    );
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  const showNotification = (incomingNotification) => {
    const notification = normalizeNotification(incomingNotification);
    const duplicateKey = buildDuplicateKey(notification);
    const now = Date.now();

    recentNotificationsRef.current.forEach((timestamp, key) => {
      if (now - timestamp > DUPLICATE_WINDOW_MS) {
        recentNotificationsRef.current.delete(key);
      }
    });

    if (recentNotificationsRef.current.has(duplicateKey)) {
      return null;
    }

    recentNotificationsRef.current.set(duplicateKey, now);
    setNotifications((current) => [...current, notification]);

    return notification.id;
  };

  const handleNotificationClick = (notification) => {
    dismissNotification(notification.id);

    if (typeof notification.onClick === 'function') {
      notification.onClick(notification);
    }

    const route = notification.route || DEFAULT_ROUTE;
    if (typeof navigate === 'function' && route) {
      navigate(route);
    }
  };

  return (
    <InAppNotificationContext.Provider
      value={{
        showNotification,
        dismissNotification,
        clearNotifications
      }}
    >
      {children}
      <InAppNotification
        notifications={notifications}
        onClose={dismissNotification}
        onClick={handleNotificationClick}
      />
    </InAppNotificationContext.Provider>
  );
};

export const useInAppNotifications = () => useContext(InAppNotificationContext);
